import CardTitleWithIcon from "@/components/company/card-title-with-icon";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CompanyDTO } from "@shaarp/common";
import { ArrowLeftIcon, ArrowRightIcon, RepeatIcon } from "lucide-react";

type CardTalentFlowProps = {
  company: CompanyDTO;
};

export default function CardTalentFlow({ company }: CardTalentFlowProps) {
  const { t } = useTranslation();
  const talentFlow = company.talentFlow;

  if (!talentFlow) return null;

  const previous = talentFlow.topPreviousCompanies ?? [];
  const next = talentFlow.topNextCompanies ?? [];

  if (previous.length === 0 && next.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3 sm:pb-4">
        <CardTitleWithIcon icon={RepeatIcon} title={t("company.signals.talentFlow.title")} />
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            "grid gap-6",
            previous.length > 0 && next.length > 0
              ? "grid-cols-1 @3xl:grid-cols-2"
              : "grid-cols-1",
          )}
        >
          {previous.length > 0 && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <ArrowRightIcon className="w-4 h-4 text-emerald-500" />
                <h4 className="text-sm font-semibold text-muted-foreground">
                  {t("company.signals.talentFlow.comingFrom")}
                </h4>
              </div>
              <div className="flex flex-col gap-1.5">
                {previous.slice(0, 8).map((entry) => (
                  <div
                    key={entry.name}
                    className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-muted/30"
                  >
                    <span className="text-sm truncate">{entry.name}</span>
                    <span className="text-xs font-semibold text-muted-foreground shrink-0 ml-2">
                      {entry.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {next.length > 0 && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <ArrowLeftIcon className="w-4 h-4 text-red-500" />
                <h4 className="text-sm font-semibold text-muted-foreground">
                  {t("company.signals.talentFlow.goingTo")}
                </h4>
              </div>
              <div className="flex flex-col gap-1.5">
                {next.slice(0, 8).map((entry) => (
                  <div
                    key={entry.name}
                    className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-muted/30"
                  >
                    <span className="text-sm truncate">{entry.name}</span>
                    <span className="text-xs font-semibold text-muted-foreground shrink-0 ml-2">
                      {entry.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
